import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridNoRecords, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { Checkbox, Input, NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { arrowRotateCwIcon, plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { JsonResponse } from 'TypeGen/json-response';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchApi } from '../../services/api';
import { Title } from '../../utils/title';
import { isNullOrWhiteSpace } from '../../utils/utils';

enum OfficeSupplyRequestStatus {
  Requested = 0,
  ManagerApproved = 10,
  Ordered = 20,
  Fulfilled = 30,
  Canceled = 70,
}

type OfficeSupplyRequestItemInput = {
  Quantity: number;
  ItemSKU: string;
  Description: string;
  Purpose: string;
  PreferredVendor: string;
  LineItemNote: string;
};

type OfficeSupplyRequestSummary = {
  OfficeSupplyRequestID: number;
  RequestedBy: string;
  Department?: string;
  Manager?: string;
  RequestDateTime: string;
  DateNeeded?: string;
  ShipToLocation: string;
  Status: OfficeSupplyRequestStatus;
  StatusDateTime: string;
  GeneralNote?: string;
  ManagerNote?: string;
  ManagerApprovedDateTime?: string;
  OrderedDateTime?: string;
  OrderedBy?: string;
  FulfilledDateTime?: string;
  FulfilledBy?: string;
  ItemCount: number;
  CanApprove: boolean;
  CanAdminister: boolean;
};

type OfficeSupplyRequestItemDetail = {
  OfficeSupplyRequestItemID: number;
  Quantity: number;
  ItemSKU?: string;
  Description: string;
  Purpose?: string;
  PreferredVendor?: string;
  LineItemNote?: string;
};

type OfficeSupplyRequestDetail = {
  OfficeSupplyRequestID: number;
  RequestedBy: string;
  Department?: string;
  Manager?: string;
  RequestDateTime: string;
  DateNeeded?: string;
  ShipToLocation: string;
  Status: OfficeSupplyRequestStatus;
  StatusDateTime: string;
  GeneralNote?: string;
  ManagerNote?: string;
  ManagerApprovedDateTime?: string;
  ManagerApprovedBy?: string;
  OrderedDateTime?: string;
  OrderedBy?: string;
  FulfilledDateTime?: string;
  FulfilledBy?: string;
  CanApprove: boolean;
  CanAdminister: boolean;
  Items: OfficeSupplyRequestItemDetail[];
};

type OfficeSupplyRequestsResponse = {
  Data?: OfficeSupplyRequestSummary[];
  Total?: number;
  HasAdminAccess?: boolean;
  HasPendingApprovals?: boolean;
  CurrentUserName?: string;
  ManagerName?: string;
  ShipToLocations?: string[];
};

type CreateRequestForm = {
  Department: string;
  ShipToLocation: string;
  DateNeeded: Date | null;
  GeneralNote: string;
  Items: OfficeSupplyRequestItemInput[];
};

function getStatusLabel(status: OfficeSupplyRequestStatus): string {
  switch (status) {
    case OfficeSupplyRequestStatus.Requested:
      return "Requested";
    case OfficeSupplyRequestStatus.ManagerApproved:
      return "Manager Approved";
    case OfficeSupplyRequestStatus.Ordered:
      return "Ordered";
    case OfficeSupplyRequestStatus.Fulfilled:
      return "Fulfilled";
    case OfficeSupplyRequestStatus.Canceled:
      return "Canceled";
    default:
      return "Unknown"; // optional fallback
  }
}

function getStatusTheme(
  status: OfficeSupplyRequestStatus
): "info" | "warning" | "success" | "primary" | "danger" {
  switch (status) {
    case OfficeSupplyRequestStatus.Requested:
      return "warning";
    case OfficeSupplyRequestStatus.ManagerApproved:
      return "primary";
    case OfficeSupplyRequestStatus.Ordered:
      return "info";
    case OfficeSupplyRequestStatus.Fulfilled:
      return "success";
    case OfficeSupplyRequestStatus.Canceled:
      return "danger";
    default:
      return "info"; // fallback
  }
}

const defaultItem: OfficeSupplyRequestItemInput = {
  Quantity: 1,
  ItemSKU: '',
  Description: '',
  Purpose: '',
  PreferredVendor: '',
  LineItemNote: '',
};

const OfficeSupplyRequests = () => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<OfficeSupplyRequestSummary[]>([]);
  //const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [shipToLocations, setShipToLocations] = useState<string[]>([]);
  const [managerName, setManagerName] = useState<string | undefined>();
  const [currentUserName, setCurrentUserName] = useState<string>('');
  const [includeMine, setIncludeMine] = useState(true);
  const [includeApprovals, setIncludeApprovals] = useState(true);
  const [statusFilter, setStatusFilter] = useState<OfficeSupplyRequestStatus | null>(null);
  const [form, setForm] = useState<CreateRequestForm>({
    Department: '',
    ShipToLocation: '',
    DateNeeded: null,
    GeneralNote: '',
    Items: [{ ...defaultItem }],
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<OfficeSupplyRequestDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailActionLoading, setDetailActionLoading] = useState(false);

  const resetForm = useCallback(() => {
    setForm({
      Department: '',
      ShipToLocation: '',
      DateNeeded: null,
      GeneralNote: '',
      Items: [{ ...defaultItem }],
    });
  }, []);

  const openCreateDialog = () => {
    resetForm();
    setFormErrors([]);
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    resetForm();
    setFormErrors([]);
    setIsCreateDialogOpen(false);
  };

  //const handleResetForm = () => {
  //  resetForm();
  //  setFormErrors([]);
  //};

  const loadRequests = useCallback(() => {
    setLoading(true);
    const payload = {
      Status: statusFilter,
      IncludeMine: includeMine,
      IncludeApprovals: includeApprovals,
    };

    fetchApi('/api/OfficeSupplyRequest/List', payload, 'POST')
      .then((response: OfficeSupplyRequestsResponse) => {
        const requestData = Array.isArray(response.Data) ? response.Data : [];
        const shipLocations = response.ShipToLocations ?? [];

        setRequests(requestData);
        // setHasAdminAccess(response.HasAdminAccess ?? false);
        setShipToLocations(shipLocations);
        setManagerName(response.ManagerName ?? undefined);
        setCurrentUserName(response.CurrentUserName ?? '');

        //if (!form.ShipToLocation && shipLocations.length > 0) {
        //  setForm(prev => ({ ...prev, ShipToLocation: shipLocations[0] }));
        //}
      })
      .catch(() => setErrorMessage('Unable to load office supply requests.'))
      .finally(() => setLoading(false));
  }, [includeApprovals, includeMine, statusFilter]); //, form.ShipToLocation

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);


  const isValidAndNotPast = (date: Date | null): boolean => {
    if (date === null) return false;

    const tz = "America/New_York";
    // startOf("day") ensures we're comparing whole dates, not times
    const todayEastern = dayjs().tz(tz).startOf("day");
    const givenEastern = dayjs(date).tz(tz).startOf("day");

    return givenEastern.isValid() && !givenEastern.isBefore(todayEastern);
  }

  const validateForm = () => {
    const errors: string[] = [];

    if (!form.ShipToLocation) {
      errors.push('Ship To Location is required.');
    }

    const hasValidItems = form.Items.some(item => item.Description.trim().length > 0 && item.Purpose.trim().length > 0 && item.Quantity > 0);
    if (!hasValidItems) {
      errors.push('At least one line item with a description, quantity, and purpose is required.');
    }

    if (isNullOrWhiteSpace(form.Department)) {
      errors.push('Department is required');
    }

    if (isValidAndNotPast(form.DateNeeded) === false) {
      errors.push('Date needed is required.');
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const submitRequest = (event: React.FormEvent) => {
    event.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    setCreating(true);

    const payload = {
      Department: form.Department,
      ShipToLocation: form.ShipToLocation,
      DateNeeded: form.DateNeeded ? dayjs(form.DateNeeded).utc().startOf('day').toISOString() : null,
      GeneralNote: form.GeneralNote,
      Items: form.Items.map(item => ({
        Quantity: item.Quantity,
        ItemSKU: item.ItemSKU,
        Description: item.Description,
        Purpose: item.Purpose,
        PreferredVendor: item.PreferredVendor,
        LineItemNote: item.LineItemNote,
      })),
    };

    fetchApi('/api/OfficeSupplyRequest/Create', payload, 'POST')
      .then(() => {
        setSuccessMessage('Request submitted successfully.');
        closeCreateDialog();
        loadRequests();
      })
      .catch(() => setErrorMessage('Unable to submit request. Please try again.'))
      .finally(() => setCreating(false));
  };

  const updateItem = (index: number, field: keyof OfficeSupplyRequestItemInput, value: string | number) => {
    setForm(prev => {
      const items = prev.Items.slice();
      items[index] = { ...items[index], [field]: value };
      return { ...prev, Items: items };
    });
  };

  const addItem = () => {
    setForm(prev => ({ ...prev, Items: [...prev.Items, { ...defaultItem }] }));
  };

  const removeItem = (index: number) => {
    setForm(prev => {
      if (prev.Items.length === 1) {
        return prev;
      }
      const items = prev.Items.slice();
      items.splice(index, 1);
      return { ...prev, Items: items };
    });
  };

  const formatDate = useCallback((value?: string) => {
    if (!value) return '';

    return dayjs(value).local().format('MM/DD/YYYY');
  }, []);

  const formatDateTime = useCallback((value?: string) => {
    if (!value) return '';

    return dayjs.utc(value).tz("America/New_York").format('MM/DD/YYYY HH:mm:ss')
  }, []);

  const openDetail = (requestId: number) => {
    setDetailId(requestId);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);

    fetchApi(`/api/OfficeSupplyRequest/Get/${requestId}`, {}, 'GET')
      .then((response: OfficeSupplyRequestDetail) => {
        setDetail(response);
      })
      .catch(() => setDetailError('Unable to load request details.'))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setDetailError(null);
  };

  const approveRequest = () => {
    if (!detail) return;
    setDetailActionLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    const payload = {
      OfficeSupplyRequestID: detail.OfficeSupplyRequestID,
      ManagerNote: detail.ManagerNote,
    };

    fetchApi('/api/OfficeSupplyRequest/Approve', payload, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          setSuccessMessage(response.ErrorMessage ?? 'Request approved.');
          loadRequests();
          openDetail(detail.OfficeSupplyRequestID);
        } else {
          setErrorMessage(response.ErrorMessage || 'Unable to approve the request.');
        }
      })
      .catch(() => setErrorMessage('Unable to approve the request.'))
      .finally(() => setDetailActionLoading(false));
  };

  const updateStatus = (status: OfficeSupplyRequestStatus) => {
    if (!detail) return;
    setDetailActionLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    const payload = {
      OfficeSupplyRequestID: detail.OfficeSupplyRequestID,
      Status: status,
    };

    fetchApi('/api/OfficeSupplyRequest/UpdateStatus', payload, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          setSuccessMessage(response.ErrorMessage ?? 'Status updated.');
          loadRequests();
          openDetail(detail.OfficeSupplyRequestID);
        } else {
          setErrorMessage(response.ErrorMessage || 'Unable to update the request status.');
        }
      })
      .catch(() => setErrorMessage('Unable to update the request status.'))
      .finally(() => setDetailActionLoading(false));
  };

  const cancelRequest = () => {
    if (!detail) return;
    if (!window.confirm('Are you sure you want to cancel this request?')) {
      return;
    }

    setDetailActionLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    const payload = {
      OfficeSupplyRequestID: detail.OfficeSupplyRequestID,
      ManagerNote: detail.ManagerNote,
    };

    fetchApi('/api/OfficeSupplyRequest/Cancel', payload, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          setSuccessMessage(response.ErrorMessage ?? 'Request canceled.');
          loadRequests();
          openDetail(detail.OfficeSupplyRequestID);
        } else {
          setErrorMessage(response.ErrorMessage || 'Unable to cancel the request.');
        }
      })
      .catch(() => setErrorMessage('Unable to cancel the request.'))
      .finally(() => setDetailActionLoading(false));
  };

  const statusButtons = useMemo(() => ([
    { status: null, label: 'All' },
    { status: OfficeSupplyRequestStatus.Requested, label: getStatusLabel(OfficeSupplyRequestStatus.Requested) },
    { status: OfficeSupplyRequestStatus.ManagerApproved, label: getStatusLabel(OfficeSupplyRequestStatus.ManagerApproved) },
    { status: OfficeSupplyRequestStatus.Ordered, label: getStatusLabel(OfficeSupplyRequestStatus.Ordered) },
    { status: OfficeSupplyRequestStatus.Fulfilled, label: getStatusLabel(OfficeSupplyRequestStatus.Fulfilled) },
    { status: OfficeSupplyRequestStatus.Canceled, label: getStatusLabel(OfficeSupplyRequestStatus.Canceled) },
  ]), []);

  return <>
    <Title string="Office Supply Requests" />
    {successMessage && <div className="alert alert-success mt-3 mb-3" role="alert">{successMessage}</div>}
    {errorMessage && <div className="alert alert-danger mt-3 mb-3" role="alert">{errorMessage}</div>}
    <br />
    <Grid
      data={requests}
      loader={loading}
      style={{ maxHeight: '80vh' }}
      onRowClick={e => openDetail(e.dataItem.OfficeSupplyRequestID)}
      rows={{ data: (rowProps) => <tr {...rowProps.trProps} style={{ ...rowProps.style, cursor: 'pointer' }}>{rowProps.children}</tr>}}
    >
      <GridToolbar>
        <Checkbox
          label="My Requests"
          checked={includeMine}
          onChange={e => setIncludeMine(e.value)}
        />&nbsp;&nbsp;
        <Checkbox
          label="Assigned to Me"
          checked={includeApprovals}
          onChange={e => setIncludeApprovals(e.value)}
        />&nbsp;&nbsp;
        <ButtonGroup>
          {statusButtons.map(button => (
            <Button
              key={button.label}
              togglable
              selected={statusFilter === button.status}
              onClick={() => setStatusFilter(prev => (prev === button.status ? null : button.status))}
            >
              {button.label}
            </Button>
          ))}
        </ButtonGroup>
        &nbsp;&nbsp;
        <Button type="button" svgIcon={arrowRotateCwIcon} onClick={loadRequests}>Refresh</Button>
        <Button type="button" themeColor="primary" svgIcon={plusIcon} onClick={openCreateDialog}>
          New Request
        </Button>
      </GridToolbar>
      <GridNoRecords>
        No requests found.
      </GridNoRecords>
      <Column field="RequestDateTime" title="Requested" cells={{ data: props => <td {...props.tdProps}>{formatDateTime(props.dataItem.RequestDateTime)}</td> }} />
      <Column field="RequestedBy" title="Requested By" />
      <Column field="Department" title="Department" />
      <Column field="ShipToLocation" title="Ship To" />
      <Column field="Status" title="Status" cells={{ data: props => (
        <td {...props.tdProps}>
          <span className={`badge badge-${getStatusTheme(props.dataItem.Status)}`} style={{ fontSize: 'small', fontWeight: '500' }}>{getStatusLabel(props.dataItem.Status)}</span>
        </td>
      ) }}
      />
      <Column field="DateNeeded" title="Needed" cells={{ data: props => <td {...props.tdProps}>{formatDate(props.dataItem.DateNeeded)}</td> }} />
      <Column field="ItemCount" title="Items" width="90px" />
      <Column field="OrderedDateTime" title="Ordered" cells={{ data: props => <td {...props.tdProps}>{formatDateTime(props.dataItem.OrderedDateTime)}</td> }} />
      <Column field="FulfilledDateTime" title="Fulfilled" cells={{ data: props => <td {...props.tdProps}>{formatDateTime(props.dataItem.FulfilledDateTime)}</td> }} />
    </Grid>

    {isCreateDialogOpen && (
      <Dialog title="Submit an Office Supply Request" onClose={closeCreateDialog} width={'90%'}>
        <form onSubmit={submitRequest}>
          {formErrors.length > 0 && (
            <div className="alert alert-warning" role="alert">
              <ul className="mb-0">
                {formErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-group">
            <label>Requested By</label>
            <Input value={currentUserName} readOnly />
          </div>
          <div className="form-group">
            <label>Approving Manager</label>
            <Input value={managerName ?? 'Manager not found'} readOnly />
          </div>
          <div className="form-group">
            <label>Department</label>
            <Input value={form.Department} onChange={e => { setForm(prev => ({ ...prev, Department: e.value as string | null ?? '' })) }} />
          </div>
          <div className="form-group">
            <label>Ship To Location</label>
            <DropDownList
              data={shipToLocations}
              value={form.ShipToLocation}
              onChange={e => setForm({ ...form, ShipToLocation: (e.value as string) })}
            // defaultItem="-"
            />
          </div>
          <div className="form-group">
            <label>Date Needed</label>
            <DatePicker value={form.DateNeeded} onChange={e => setForm(prev => ({ ...prev, DateNeeded: e.value }))} />
          </div>
          <div className="form-group">
            <label>Order Note</label>
            <TextArea value={form.GeneralNote} rows={3} onChange={e => setForm(prev => ({ ...prev, GeneralNote: e.value ?? '' }))} />
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Items</h5>
            <Button type="button" icon="plus" svgIcon={plusIcon} onClick={addItem}>
              Add Item
            </Button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Qty</th>
                  <th>Description</th>
                  <th>Item/SKU</th>
                  <th>Purpose</th>
                  <th>Preferred Vendor</th>
                  <th>Note</th>
                  <th style={{ width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.Items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <NumericTextBox
                        value={item.Quantity}
                        min={0}
                        step={1}
                        format="n0"
                        onChange={e => updateItem(index, 'Quantity', e.value ?? 0)}
                      />
                    </td>
                    <td>
                      <Input value={item.Description} onChange={e => updateItem(index, 'Description', e.target.value as string)} />
                    </td>
                    <td>
                      <Input value={item.ItemSKU} onChange={e => updateItem(index, 'ItemSKU', e.target.value as string)} />
                    </td>
                    <td>
                      <Input value={item.Purpose} onChange={e => updateItem(index, 'Purpose', e.target.value as string)} />
                    </td>
                    <td>
                      <Input value={item.PreferredVendor} onChange={e => updateItem(index, 'PreferredVendor', e.target.value as string)} />
                    </td>
                    <td>
                      <Input value={item.LineItemNote} onChange={e => updateItem(index, 'LineItemNote', e.target.value as string)} />
                    </td>
                    <td className="text-center">
                      <Button
                        type="button"
                        svgIcon={trashIcon}
                        title="Remove"
                        onClick={() => removeItem(index)}
                        disabled={form.Items.length === 1}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogActionsBar>
            <Button type="button" onClick={closeCreateDialog} disabled={creating}>
              Cancel
            </Button>
            {/*<Button type="button" svgIcon={arrowRotateCwIcon} onClick={handleResetForm} disabled={creating}>*/}
            {/*  Reset*/}
            {/*</Button>*/}
            <Button type="submit" themeColor="primary" disabled={creating}>
              Submit Request
            </Button>
          </DialogActionsBar>
        </form>
      </Dialog>
    )}

    {detailId !== null && (
      <Dialog title={`Request #${detailId}`} onClose={closeDetail} width={900}>
        {detailLoading ? (
          <div className="text-center my-5">
            <Loader size="large" type="infinite-spinner" />
          </div>
        ) : detailError ? (
          <div className="alert alert-danger" role="alert">{detailError}</div>
        ) : detail ? (
          <div className="office-supply-detail">
            <div className="mb-3 d-flex justify-content-between align-items-start">
              <div>
                <span className={`badge badge-${getStatusTheme(detail.Status)}`} style={{ fontSize: 'small', fontWeight: '500' }}>{getStatusLabel(detail.Status)}</span>
                <div className="text-muted small">Last updated {formatDateTime(detail.StatusDateTime)}</div>
              </div>

            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <dl className="row mb-0">
                  <dt className="col-sm-5">Requested By</dt>
                  <dd className="col-sm-7">{detail.RequestedBy}</dd>
                  <dt className="col-sm-5">Department</dt>
                  <dd className="col-sm-7">{detail.Department || '—'}</dd>
                  <dt className="col-sm-5">Ship To</dt>
                  <dd className="col-sm-7">{detail.ShipToLocation}</dd>
                  <dt className="col-sm-5">Date Needed</dt>
                  <dd className="col-sm-7">{formatDate(detail.DateNeeded)}</dd>
                </dl>
              </div>
              <div className="col-md-6">
                <dl className="row mb-0">
                  <dt className="col-sm-5">Manager</dt>
                  <dd className="col-sm-7">{detail.Manager || '—'}</dd>
                  <dt className="col-sm-5">Manager Approved</dt>
                  <dd className="col-sm-7">{detail.ManagerApprovedDateTime ? `${formatDateTime(detail.ManagerApprovedDateTime)}${detail.ManagerApprovedBy ? ` by ${detail.ManagerApprovedBy}` : ''}` : '—'}</dd>
                  <dt className="col-sm-5">Ordered</dt>
                  <dd className="col-sm-7">{detail.OrderedDateTime ? `${formatDateTime(detail.OrderedDateTime)}${detail.OrderedBy ? ` by ${detail.OrderedBy}` : ''}` : '—'}</dd>
                  <dt className="col-sm-5">Fulfilled</dt>
                  <dd className="col-sm-7">{detail.FulfilledDateTime ? `${formatDateTime(detail.FulfilledDateTime)}${detail.FulfilledBy ? ` by ${detail.FulfilledBy}` : ''}` : '—'}</dd>
                </dl>
              </div>
            </div>
            {detail.GeneralNote && (
              <div className="mb-3">
                <h6>Order Note</h6>
                <p className="mb-0">{detail.GeneralNote}</p>
              </div>
            )}
            <div className="mb-3">
              <h6>Line Items</h6>
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th style={{ width: '70px' }}>Qty</th>
                      <th>Description</th>
                      <th>Item/SKU</th>
                      <th>Purpose</th>
                      <th>Preferred Vendor</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.Items.map(item => (
                      <tr key={item.OfficeSupplyRequestItemID}>
                        <td>{item.Quantity}</td>
                        <td>{item.Description}</td>
                        <td>{item.ItemSKU || '—'}</td>
                        <td>{item.Purpose || '—'}</td>
                        <td>{item.PreferredVendor || '—'}</td>
                        <td>{item.LineItemNote || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {detail.CanApprove ? (
              <div className="mb-3">
                <h6>Manager Note</h6>
                <TextArea
                  value={detail.ManagerNote || ''}
                  rows={3}
                  onChange={e => setDetail(prev => (prev ? { ...prev, ManagerNote: e.value ?? '' } : prev))}
                />
              </div>
            ) : detail.ManagerNote ? (
              <div className="mb-3">
                <h6>Manager Note</h6>
                <p className="mb-0">{detail.ManagerNote}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        <DialogActionsBar>
          {detail && detail.Status === OfficeSupplyRequestStatus.Requested && (detail.CanApprove || detail.CanAdminister) && (
            <Button
              themeColor="error"
              onClick={cancelRequest}
              disabled={detailActionLoading}
            >
              Cancel Request
            </Button>
          )}
          {detail && detail.CanApprove && (
            <Button
              themeColor="success"
              onClick={approveRequest}
              disabled={detailActionLoading}
            >
              Approve Request
            </Button>
          )}
          {detail && detail.CanAdminister && detail.Status === OfficeSupplyRequestStatus.ManagerApproved && (
            <Button
              themeColor="primary"
              onClick={() => updateStatus(OfficeSupplyRequestStatus.Ordered)}
              disabled={detailActionLoading}
            >
              Mark Ordered
            </Button>
          )}
          {detail && detail.CanAdminister && detail.Status === OfficeSupplyRequestStatus.Ordered && (
            <Button
              themeColor="success"
              onClick={() => updateStatus(OfficeSupplyRequestStatus.Fulfilled)}
              disabled={detailActionLoading}
            >
              Mark Fulfilled
            </Button>
          )}
          <Button onClick={closeDetail} disabled={detailActionLoading}>Close</Button>
        </DialogActionsBar>
      </Dialog>
    )}
  </>
};

export default OfficeSupplyRequests;
